/**
 * Formato en español.
 *
 * El dinero llega de la API como string («1250.00») y se formatea aquí. Se convierte a
 * `number` solo en el último paso, justo para dárselo a `Intl.NumberFormat`: en ese punto ya
 * no hay más aritmética que pueda arrastrar un error de coma flotante.
 */
import type { Money } from '@naar/shared';

/**
 * `useGrouping: 'always'` no es opcional aquí.
 *
 * El CLDR de `es-ES` define `minimumGroupingDigits: 2`, así que por defecto un número de
 * cuatro cifras sale SIN separador de millar: `2344 €` en lugar de `2.344 €`. El maquetado
 * agrupa siempre («2.344 €», «5.375 €»), que es lo habitual en documentación inmobiliaria, y
 * mezclar ambos estilos en la misma tabla se ve mal. Con `'always'` se fuerza el separador.
 */
const eur0 = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  useGrouping: 'always',
});

const eur2 = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
});

/** `1.250 €` — el formato de los KPI y las tablas del maquetado. */
export function formatEur(value: Money | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${eur0.format(Math.round(Number(value)))} €`;
}

/** `−72,90 €` — para importes con céntimos, como los movimientos bancarios. */
export function formatEurCents(value: Money | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${eur2.format(Number(value))} €`;
}

/**
 * `17,9 €` — importes pequeños donde el decimal SÍ significa algo, como el ingreso por m².
 * Redondear a entero convertiría 17,90 en 18 y perdería la única cifra informativa.
 */
export function formatEurPrecise(
  value: Money | number | null | undefined,
  decimals = 1,
): string {
  if (value === null || value === undefined) return '—';
  const formatter = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: 'always',
  });
  return `${formatter.format(Number(value))} €`;
}

/**
 * Importe de movimiento con signo. El maquetado usa el signo menos tipográfico «−» (U+2212),
 * no el guion del teclado, y solo muestra céntimos cuando los hay.
 */
export function formatSignedEur(value: Money | number): string {
  const amount = Number(value);
  const hasCents = Math.abs(amount * 100) % 100 !== 0;
  const magnitude = hasCents ? eur2.format(Math.abs(amount)) : eur0.format(Math.abs(amount));
  const sign = amount < 0 ? '−' : '+';
  return `${sign}${magnitude} €`;
}

/** `4,2%` — coma decimal, y sin decimales si el valor es entero. */
export function formatPercent(
  value: number | null | undefined,
  decimals = 1,
): string {
  if (value === null || value === undefined) return '—';
  if (Number.isInteger(value)) return `${value}%`;
  return `${value.toFixed(decimals).replace('.', ',')}%`;
}

/** `▲ 4,2%` / `▼ 1,8%` — el indicador de tendencia. */
export function formatTrend(percent: number | null | undefined, decimals = 1): string {
  if (percent === null || percent === undefined) return '—';
  const arrow = percent > 0 ? '▲' : percent < 0 ? '▼' : '■';
  return `${arrow} ${formatPercent(Math.abs(percent), decimals)}`;
}

/** `▲ +254.928 €` — variación en valor absoluto. */
export function formatTrendAmount(amount: Money | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const value = Number(amount);
  const arrow = value >= 0 ? '▲' : '▼';
  return `${arrow} ${value >= 0 ? '+' : '−'}${eur0.format(Math.abs(Math.round(value)))} €`;
}

/** `197` — superficies y enteros sin unidad. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return eur0.format(value);
}

/** `05/09/24` — el formato de fecha de la tabla de documentos. */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(2);
  return `${day}/${month}/${year}`;
}

/** `02/06` — el formato de fecha de la tabla de movimientos. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/** Porcentaje ya calculado por la API, listo para una anchura o altura CSS. */
export function cssPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0%';
  return `${Math.max(0, Math.min(100, value))}%`;
}
